import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSearch, faComments, faEye, faClock, faTag } from '@fortawesome/free-solid-svg-icons';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/prompts');
      setTemplates(res.data);
      
      // Извлекаем уникальные категории
      const uniqueCategories = [...new Set(res.data.map(template => template.category))];
      setCategories(uniqueCategories);
      
      setLoading(false);
    } catch (err) {
      setError('Не удалось загрузить шаблоны');
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот шаблон?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/prompts/${id}`);
      setTemplates(templates.filter(template => template._id !== id));
    } catch (err) {
      setError('Не удалось удалить шаблон');
    }
  };

  // Фильтрация шаблонов
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(search.toLowerCase()) ||
                        (template.description && template.description.toLowerCase().includes(search.toLowerCase())) ||
                        (template.category && template.category.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = filter === 'all' || template.category === filter;
    
    return matchesSearch && matchesCategory;
  });

  const handleUseInChat = async (templateId) => {
    try {
      // Создаем новый чат с шаблоном
      const res = await axios.post('/api/chat', { 
        title: `Чат с шаблоном: ${templates.find(t => t._id === templateId)?.title}`,
        promptId: templateId
      });
      const newChat = res.data;
      
      // Переходим в чат и передаем ID шаблона
      window.location.href = `/chat/${newChat._id}?template=${templateId}`;
    } catch (error) {
      console.error('Ошибка при создании чата с шаблоном:', error);
      // Fallback - просто перейти в чат с параметром prompt
      window.location.href = `/chat?prompt=${templateId}`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const getTemplateTypeLabel = (type) => {
    const types = {
      post: 'Пост',
      note: 'Заметка', 
      interview: 'Интервью',
      summary: 'Саммари',
      editing: 'Редактирование'
    };
    return types[type] || type;
  };

  return (
    <div className="templates-container">
      <div className="templates-header">
        <h1>Шаблоны промптов</h1>
        <Link to="/templates/new" className="btn btn-primary">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Создать шаблон
        </Link>
      </div>
      
      {error && <div className="alert alert-danger">{error}</div>}
      
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Поиск шаблонов..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-6">
          <select
            className="form-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">Все категории</option>
            {categories.map((category, index) => (
              <option key={index} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-5">
          <p className="lead">Шаблоны не найдены</p>
          <Link to="/templates/new" className="btn btn-primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Создать первый шаблон
          </Link>
        </div>
      ) : (
        <div className="row">
          {filteredTemplates.map(template => (
            <div key={template._id} className="col-md-6 col-lg-4 mb-4">
              <div className="card template-card h-100">
                <div className="card-body d-flex flex-column">
                  <div className="template-header mb-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <span className="template-category">
                        <FontAwesomeIcon icon={faTag} className="me-1" />
                        {template.category}
                      </span>
                      <span className="template-type badge bg-light text-dark">
                        {getTemplateTypeLabel(template.promptType)}
                      </span>
                    </div>
                    <h5 className="card-title mb-2">{template.title}</h5>
                    {template.description && (
                      <p className="card-text text-muted mb-2">{template.description}</p>
                    )}
                  </div>
                  
                  <div className="template-info mb-3 flex-grow-1">
                    <div className="d-flex justify-content-between text-muted small">
                      <span>
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {formatDate(template.createdAt)}
                      </span>
                      <span>
                        {template.blocks?.length || 0} блоков
                      </span>
                    </div>
                  </div>
                  
                  <div className="template-actions d-flex justify-content-between mt-auto">
                    <div className="d-flex gap-2">
                      <button 
                        onClick={() => handleUseInChat(template._id)} 
                        className="btn btn-sm btn-primary"
                      >
                        <FontAwesomeIcon icon={faComments} className="me-1" />
                        В чат
                      </button>
                      <Link 
                        to={`/templates/edit/${template._id}`} 
                        className="btn btn-sm btn-outline-secondary"
                        title="Предпросмотр"
                      >
                        <FontAwesomeIcon icon={faEye} />
                    </Link>
                    </div>
                    <div className="d-flex gap-1">
                      <Link 
                        to={`/templates/edit/${template._id}`} 
                        className="btn btn-sm btn-outline-secondary"
                        title="Редактировать"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteTemplate(template._id)}
                        title="Удалить"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Templates; 